import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import NoIndex from "@/components/NoIndex";
import useLiveStream from "@/lib/useLiveStream";
import { playDing, unlockAudio } from "@/lib/synth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const CHECKLIST = [
  "Projector mirrored and the display page is FULLSCREEN (F11)",
  "Clicked once on the display page so the ding + notes can play",
  "Moderator console open on your device, logged in",
  "Scanned the QR yourself and sent one test word end to end",
  "Location + parsha filled in (Slides → Show details)",
  "Act 2 Scene 1 wheel songs typed in",
  "Recap reset before doors open",
];

function Row({ label, value, ok, hint }) {
  return (
    <div
      className="flex items-center justify-between gap-4 px-5 py-3 rounded-xl"
      style={{ backgroundColor: "rgba(255,255,255,0.75)", border: "1px solid rgba(26,21,35,0.07)" }}
      data-testid={`check-row-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] opacity-60">{label}</p>
        <p className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{value}</p>
        {hint ? <p className="text-[11px] opacity-50 mt-0.5">{hint}</p> : null}
      </div>
      <span
        className="text-xs px-3 py-1 rounded-full whitespace-nowrap"
        style={{
          backgroundColor: ok ? "rgba(63,174,106,0.18)" : "rgba(226,72,60,0.18)",
          color: ok ? "#7ED9A6" : "#E86F4A",
          fontWeight: 700,
          letterSpacing: "0.12em",
        }}
      >
        {ok ? "READY" : "CHECK"}
      </span>
    </div>
  );
}

/** Pre-show smoke test: one screen Dan can look at before doors open. */
export default function PreShowCheck() {
  const { slug } = useParams();
  const { connected } = useLiveStream(slug);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [latency, setLatency] = useState(null);
  const [audioOk, setAudioOk] = useState(false);
  const [ticked, setTicked] = useState({});

  const audienceUrl = useMemo(() => `${window.location.origin}/live/${slug}`, [slug]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const t0 = performance.now();
      try {
        const { data } = await ax.get(`/admin/live/events/${slug}/stats`, { params: { t: Date.now() } });
        if (cancelled) return;
        setLatency(Math.round(performance.now() - t0));
        setStats(data);
        setError("");
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 401 ? "Log in on /app/login first." : "Couldn't reach the server.");
      }
    };
    load();
    const id = window.setInterval(load, 5000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [slug]);

  const testAudio = () => {
    unlockAudio();
    playDing(0.8);
    setAudioOk(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF8F5", color: "#1A1523" }} data-live-dark="" data-testid="live-check-page">
      <NoIndex title="Compose Yourself! by Dan Berry" />
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-6">
        <header>
          <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "#8A6514" }}>Pre-show check</p>
          <h1 className="text-4xl sm:text-5xl mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {stats?.name || slug}
          </h1>
        </header>

        {error ? (
          <p className="text-sm px-5 py-4 rounded-xl" style={{ backgroundColor: "rgba(226,72,60,0.14)", color: "#E86F4A" }} data-testid="check-error">
            {error}
          </p>
        ) : null}

        <section className="space-y-2">
          <Row label="Live feed" value={connected ? "Connected" : "Not connected"} ok={connected} hint="Server-sent events — the projector and phones use this." />
          <Row label="Server round-trip" value={latency === null ? "—" : `${latency} ms`} ok={latency !== null && latency < 1200} hint="Under ~1.2s is comfortable on venue wifi." />
          <Row label="Event" value={stats ? (stats.is_active ? "Accepting words" : "Paused") : "—"} ok={!!stats?.is_active} />
          <Row label="Current slide" value={stats?.current_slide_key || "—"} ok={!!stats?.current_slide_key} hint={`Language: ${stats?.mode || "—"}`} />
          <Row label="Show details" value={`${stats?.location_name || "no location"} · ${stats?.parsha_name || "no parsha"}`} ok={!!(stats?.location_name && stats?.parsha_name)} hint="Used on the Act 3 Scene 1 staff and the recap." />
          <Row label="Audio on this screen" value={audioOk ? "Unlocked" : "Not tested"} ok={audioOk} hint="Browsers block sound until you interact — tap the button below on the projector machine." />
          <Row label="People joined" value={String(stats?.participants ?? "—")} ok={(stats?.participants ?? 0) >= 0} />
          <Row label="Words waiting" value={String(stats?.pending_submissions ?? "—")} ok hint={`${stats?.audience_picks ?? 0} audience picks pushed so far`} />
        </section>

        <div className="flex flex-wrap gap-3">
          <Button onClick={testAudio} data-testid="check-audio-btn" className="rounded-full" style={{ backgroundColor: "transparent", color: "#8A6514", border: "1px solid #D99B26", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Test sound
          </Button>
          <Button asChild variant="outline" className="rounded-full border-[#D6CBB8]">
            <a href={`/live/${slug}/display`} target="_blank" rel="noreferrer" data-testid="check-open-display">Open display →</a>
          </Button>
          <Button asChild variant="outline" className="rounded-full border-[#D6CBB8]">
            <a href={`/live/${slug}/admin`} target="_blank" rel="noreferrer" data-testid="check-open-admin">Open console →</a>
          </Button>
        </div>

        <section
          className="rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-center"
          style={{ backgroundColor: "rgba(255,255,255,0.75)", border: "1px solid rgba(26,21,35,0.07)" }}
        >
          <div className="p-3 rounded-xl" style={{ backgroundColor: "#1A1523" }}>
            <QRCodeSVG value={audienceUrl} size={132} bgColor="#1A1523" fgColor="#FAF8F5" level="M" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] opacity-60">Audience link</p>
            <p className="text-xl mt-1" style={{ fontFamily: "'Courier Prime', monospace" }} data-testid="check-audience-url">
              {audienceUrl.replace(/^https?:\/\//, "")}
            </p>
            <p className="text-[11px] opacity-50 mt-2">Scan it yourself and send one word before doors open.</p>
          </div>
        </section>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.28em] opacity-60">Run-of-show checklist</p>
          {CHECKLIST.map((item, i) => (
            <label key={item} className="flex items-start gap-3 px-5 py-3 rounded-xl cursor-pointer" style={{ backgroundColor: "rgba(255,255,255,0.7)" }}>
              <input
                type="checkbox"
                checked={!!ticked[i]}
                onChange={() => setTicked((t) => ({ ...t, [i]: !t[i] }))}
                data-testid={`check-item-${i}`}
                className="mt-1"
              />
              <span className="text-base" style={{ fontFamily: "'Cormorant Garamond', serif", opacity: ticked[i] ? 0.45 : 1, textDecoration: ticked[i] ? "line-through" : "none" }}>
                {item}
              </span>
            </label>
          ))}
        </section>
      </div>
    </div>
  );
}
